import { UserStatus } from '@shared/constants';

/**
 * Normalize query: remove @ → lowercase → trim
 */
export const normalizeQuery = (query: string): string => {
  return query.replace(/^@/, '').toLowerCase().trim();
};

/**
 * Get fallback name (name or email)
 */
const getFallbackName = (user: { name?: string; email?: string }): string => {
  return user.name || user.email || '';
};

/**
 * Get mention text: username (lowercase) or fallback (lowercase)
 */
export const getUserMentionText = (user: {
  username?: string;
  name?: string;
  email?: string;
}): string => {
  return (user.username || getFallbackName(user)).toLowerCase();
};

/**
 * Check if user is deactivated
 */
export const isUserDeactivated = (userStatus?: string): boolean => {
  return userStatus?.toLowerCase() === UserStatus.INACTIVE;
};

/**
 * Find exact username match (case insensitive) for auto-selection
 */
export const findExactUsernameMatch = <
  T extends { username?: string; id?: string },
>(
  users: T[],
  query: string,
): T | null => {
  if (!query || users.length === 0) return null;

  const normalizedQuery = normalizeQuery(query);

  return (
    users.find((user) => user.username?.toLowerCase() === normalizedQuery) ||
    null
  );
};
/**
 * Replace partial mention with full mention text and clean up duplicate spaces
 *
 * Examples:
 * - "Deactivate @jo account" → "Deactivate @john.doe account"
 * - "@jo" → "@john.doe "
 */
export const handleMentionSelection = (
  mentionText: string,
  replaceStart: number,
  replaceEnd: number,
  search: string,
  setSearch: (value: string) => void,
  setCursorPosition: (position: number) => void,
  closeMention: () => void,
  onInputChange?: (searchQuery: string) => void,
): void => {
  // Replace partial mention with full mention + space
  const newValue =
    search.substring(0, replaceStart) +
    mentionText +
    ' ' +
    search.substring(replaceEnd);

  // Clean up duplicate spaces
  const cleanedValue = newValue.replace(/\s{2,}/g, ' ');

  // Position cursor after mention + space
  const newCursorPosition = replaceStart + mentionText.length + 1;

  setSearch(cleanedValue);
  setCursorPosition(newCursorPosition);
  closeMention();
  onInputChange?.(cleanedValue);
};
